import type {
  AccountEntryType,
  CashMovementType,
  CustomerTaxCondition,
  DocType,
  MovementType,
  PaymentMethod,
  PermissionSet,
  Role,
  UnitType,
  DeviceClock,
} from '@pos/core';

export type Rubro = 'kiosco' | 'almacen' | 'perfumeria' | 'supermercado' | 'ferreteria' | 'ropa' | 'general';

export const RUBROS: Record<Rubro, string> = {
  kiosco: 'Kiosco',
  almacen: 'Almacén',
  perfumeria: 'Perfumería',
  supermercado: 'Supermercado',
  ferreteria: 'Ferretería',
  ropa: 'Ropa',
  general: 'Otro',
};

export interface BusinessTheme {
  primary?: string;
}

export interface BusinessSettings {
  /** Mostrar centavos en precios y totales. */
  show_cents?: boolean;
  /** Punto de venta: medios de pago, recargos, redondeo, ticket (ver normalizePosSettings). */
  pos?: unknown;
}

export interface BusinessRow {
  id: string;
  name: string;
  slug: string;
  rubro: Rubro;
  logo_path: string | null;
  theme: BusinessTheme;
  settings: BusinessSettings;
  contact_phone: string | null;
  whatsapp: string | null;
  email: string | null;
  address: string | null;
  cuit: string | null;
  tax_condition: 'monotributo' | 'responsable_inscripto' | 'exento' | null;
  timezone: string;
  currency: string;
  lifecycle: 'activo' | 'baja_solicitada' | 'pendiente_borrado' | 'borrado';
  created_at: string;
  updated_at: string;
  server_xid?: string;
}

export interface MembershipRow {
  id: string;
  business_id: string;
  user_id: string;
  role: Role;
  display_name: string;
  username: string | null;
  permissions: PermissionSet;
  active: boolean;
  /** Eliminado (sale de la lista; la fila queda para el historial). */
  deleted_at?: string | null;
  created_at: string;
  updated_at: string;
  server_xid?: string;
}

export interface DeviceRow {
  id: string;
  business_id: string;
  name: string;
  code: number;
  last_seen_at: string | null;
  revoked_at: string | null;
  created_at: string;
  updated_at: string;
  server_xid?: string;
}

// ---------------------------------------------------------------------------
// Catálogo
// ---------------------------------------------------------------------------

interface SyncedRow {
  id: string;
  business_id: string;
  created_by?: string | null;
  created_at: string;
  updated_at: string;
  server_xid?: string;
}

export interface CategoryRow extends SyncedRow {
  name: string;
  sort: number;
  deleted_at: string | null;
}

export interface SupplierRow extends SyncedRow {
  name: string;
  phone: string | null;
  email: string | null;
  cuit: string | null;
  notes: string | null;
  deleted_at: string | null;
}

export interface ProductRow extends SyncedRow {
  name: string;
  category_id: string | null;
  supplier_id: string | null;
  unit_type: UnitType;
  tax_bps: number;
  image_path: string | null;
  has_variants: boolean;
  /** Nombres de los atributos de variante, p. ej. ["Talle", "Color"]. */
  variant_attributes: string[];
  active: boolean;
  notes: string | null;
  deleted_at: string | null;
}

export interface VariantRow extends SyncedRow {
  product_id: string;
  attributes: Record<string, string>;
  sku: string | null;
  barcode: string | null;
  /** Centavos. */
  price: number;
  /** Milésimas de unidad. */
  min_stock: number | null;
  is_default: boolean;
  active: boolean;
  sort: number;
  deleted_at: string | null;
}

/** Costo de una variante (id = id de la variante). Solo existe en dispositivos del dueño. */
export interface VariantCostRow extends SyncedRow {
  /** Centavos. */
  cost: number;
}

// ---------------------------------------------------------------------------
// Stock
// ---------------------------------------------------------------------------

/** Resumen de stock del servidor (por variante y ubicación). */
export interface StockLevelRow {
  id: string;
  business_id: string;
  variant_id: string;
  location_id: string;
  qty_milli: number;
  movements_count: number;
  /** Lleva control de stock (tuvo stock inicial, compras, ajustes o conteos; las ventas solas no cuentan). */
  tracked?: boolean;
  updated_at: string;
  server_xid?: string;
}

/** Movimiento creado en este dispositivo (los de otros dispositivos llegan en el resumen). */
export interface StockMovementRow {
  id: string;
  business_id: string;
  variant_id: string;
  location_id?: string | null;
  type: MovementType;
  qty_milli: number;
  reason: string | null;
  ref_type: 'purchase' | 'sale' | 'count' | null;
  ref_id: string | null;
  occurred_at: string;
  device_id: string | null;
  created_by?: string | null;
  created_at: string;
  /** Transacción del servidor que lo aplicó (la informa sync_push). Vacío = todavía no llegó. */
  server_xid?: string;
}

export interface PurchaseRow {
  id: string;
  business_id: string;
  supplier_id: string | null;
  invoice_number: string | null;
  occurred_at: string;
  notes: string | null;
  /** Centavos; null si se cargó sin costos. */
  total: number | null;
  voided_at: string | null;
  void_reason: string | null;
  device_id: string | null;
  created_by?: string | null;
  created_at: string;
  updated_at: string;
  server_xid?: string;
}

export interface PurchaseItemRow {
  id: string;
  business_id: string;
  purchase_id: string;
  variant_id: string;
  qty_milli: number;
  unit_cost: number | null;
  created_by?: string | null;
  created_at: string;
  updated_at: string;
  server_xid?: string;
}

// ---------------------------------------------------------------------------
// Ventas (solo las de este dispositivo; el historial completo se consulta online)
// ---------------------------------------------------------------------------

export interface SaleItemDoc {
  id: string;
  /** Id del movimiento de stock que genera (el servidor lo crea con este mismo id). */
  movement_id: string;
  variant_id: string;
  /** Nombre al momento de vender. */
  description: string;
  unit_type: UnitType;
  qty_milli: number;
  unit_price: number;
  /** Descuento total de la línea (propio + parte del descuento general). */
  discount: number;
  tax_bps: number;
  net: number;
  tax: number;
  total: number;
  /** Devoluciones: línea de la venta original. */
  original_item_id?: string | null;
}

export interface SalePaymentDoc {
  id: string;
  method: PaymentMethod;
  base: number;
  adjustment: number;
  rounding: number;
  amount: number;
  tendered: number | null;
  reference?: string | null;
  installments?: number | null;
}

/** Venta o devolución con sus ítems y pagos: viaja al servidor como una sola operación. */
export interface SaleRow {
  id: string;
  business_id: string;
  kind: 'sale' | 'return';
  original_sale_id: string | null;
  /** "0001-00000123". */
  number: string;
  device_id: string | null;
  seller_id: string;
  subtotal: number;
  discount_total: number;
  adjustment_total: number;
  rounding_total: number;
  tax_total: number;
  total: number;
  notes: string | null;
  occurred_at: string;
  created_at: string;
  customer_id?: string | null;
  cash_session_id?: string | null;
  items: SaleItemDoc[];
  payments: SalePaymentDoc[];
  /** Transacción del servidor que la registró (vacío = todavía no se envió). */
  server_xid?: string;
}

// ---------------------------------------------------------------------------
// Clientes, caja y cuenta corriente
// ---------------------------------------------------------------------------

export interface CustomerRow extends SyncedRow {
  name: string;
  phone: string | null;
  email: string | null;
  doc_type: DocType | null;
  doc_number: string | null;
  tax_condition: CustomerTaxCondition;
  address: string | null;
  /** Centavos; null = sin límite. */
  credit_limit: number | null;
  notes: string | null;
  active: boolean;
  deleted_at: string | null;
}

/** Caja del negocio (física): la usan todos los dispositivos. */
export interface RegisterRow {
  id: string;
  business_id: string;
  name: string;
  is_default: boolean;
  /** Eliminada (archivada): no se usa más, queda para el historial. */
  archived_at: string | null;
  created_at: string;
  updated_at: string;
  server_xid?: string;
}

export interface CashSessionRow {
  id: string;
  business_id: string;
  register_id?: string | null;
  /** Dispositivo que la abrió. */
  device_id: string | null;
  /** Caja solo de este dispositivo (se abrió sin conexión): no la comparten los demás. */
  device_only?: boolean;
  opened_by: string | null;
  opened_at: string;
  opening_amount: number;
  closed_by?: string | null;
  closed_at: string | null;
  counted_cash: number | null;
  /** Los calcula el servidor al cerrar (en el dispositivo se muestra el cálculo local hasta que llegan). */
  expected_cash?: number | null;
  difference?: number | null;
  close_notes: string | null;
  created_at: string;
  updated_at: string;
  server_xid?: string;
}

/** Ingreso, retiro o gasto de efectivo (solo los de este dispositivo). */
export interface CashMovementRow {
  id: string;
  business_id: string;
  session_id: string;
  type: CashMovementType;
  amount: number;
  reason: string;
  occurred_at: string;
  device_id: string | null;
  created_by?: string | null;
  created_at: string;
  server_xid?: string;
}

/** Movimiento de cuenta corriente creado en este dispositivo (los demás llegan en el saldo). */
export interface AccountEntryRow {
  id: string;
  business_id: string;
  customer_id: string;
  type: AccountEntryType;
  /** Positivo: el cliente debe más. */
  amount: number;
  method: PaymentMethod | null;
  sale_id: string | null;
  cash_session_id: string | null;
  note: string | null;
  occurred_at: string;
  device_id: string | null;
  created_by?: string | null;
  created_at: string;
  server_xid?: string;
}

/** Saldo de un cliente según el servidor. */
export interface CustomerBalanceRow {
  id: string;
  business_id: string;
  customer_id: string;
  balance: number;
  entries_count: number;
  updated_at: string;
  server_xid?: string;
}

/** Verificador guardado en ESTE dispositivo para poder entrar sin internet. */
export interface LocalCredential {
  /** `${kind}:${business_id}:${login_id}` */
  id: string;
  kind: 'password' | 'pin';
  business_id: string;
  user_id: string;
  membership_id: string;
  /** Email (dueño) o usuario (empleado), normalizado. */
  login_id: string;
  verifier: string;
  updated_at: string;
  /** Intentos fallidos sin conexión (mismo límite que el servidor). */
  failed_attempts?: number;
  locked_until?: string | null;
}

export interface CursorRow {
  /** `${business_id}:${table}` */
  key: string;
  xid: string;
  id: string;
}

/** Identidad de este dispositivo, obtenida al habilitarlo. */
export interface DeviceIdentity {
  id: string;
  business_id: string;
  code: number;
  name: string;
  secret: string;
}

/** Sesión local (quién está usando el dispositivo ahora). */
export interface LocalSession {
  user_id: string;
  membership_id: string;
  business_id: string;
  /** online: validado contra el servidor. offline: validado con el verificador local. */
  mode: 'online' | 'offline';
  started_at: string;
}

/**
 * Cambio rechazado por el servidor y guardado en su cuarentena (etapa 7). El dueño lo aplica o
 * lo descarta; el dispositivo que lo hizo se entera al bajar esta tabla. id = op_id.
 */
export interface SyncRejectionRow {
  id: string;
  business_id: string;
  table_name: string;
  kind: 'insert' | 'patch';
  row_id: string;
  payload: Record<string, unknown>;
  base: Record<string, unknown> | null;
  op_created_at: string | null;
  device_id: string | null;
  author_id: string | null;
  created_by: string | null;
  error: string;
  code: string | null;
  attempts: number;
  status: 'pending' | 'applied' | 'discarded';
  rejected_at: string;
  resolved_at: string | null;
  resolved_by: string | null;
  applied_xid: string | null;
  created_at: string;
  updated_at: string;
  server_xid: string;
}

export interface MetaMap {
  device: DeviceIdentity;
  session: LocalSession;
  /** Último número de venta y de devolución usados en este dispositivo. */
  sale_seq: number;
  return_seq: number;
  /** Último estado de licencia confirmado por el servidor (para funcionar sin conexión). */
  license: CachedLicense;
  /** Reloj protegido para la licencia (etapa 8B). */
  clock: DeviceClock;
  /** Caja que usa este dispositivo (si el negocio tiene varias). */
  cash_register: string;
  /** Toma de inventario en curso en este dispositivo (se guarda sola mientras se cuenta). */
  count_draft: CountDraft;
}

export interface CountDraft {
  started_at: string;
  /** Por variante: lo escrito y el stock del sistema cuando se anotó (para avisar si cambió). */
  entries: Record<string, { value: string; system: number }>;
}

export interface CachedLicense {
  business_id: string;
  status: string;
  plan: { code: string; name: string; limits: Record<string, number | null>; features: Record<string, unknown> };
  trial_until: string | null;
  paid_until: string | null;
  grace_days: number;
  readonly_days: number;
  manual_override: string | null;
  /** Plan sin vencimiento (cuentas sin cargo). */
  no_expiry?: boolean;
  server_time: string;
  usage: { employees: number; devices: number; registers: number };
  /** Hora del dispositivo cuando se confirmó. */
  fetched_at: string;
  /** Credencial firmada (etapa 8B). Se vuelve a verificar al abrir: si fue alterada, no vale. */
  token?: string | null;
}

export interface MetaEntry<K extends keyof MetaMap = keyof MetaMap> {
  key: K;
  value: MetaMap[K];
}
